/**
 * @file linkSettings.ts
 * @brief Pure form model for Super Admin → Links & company info (W19, B23)
 *
 * @details
 * GET /api/admin/link-settings sends sections of settings, each
 * `{ key, label, kind, options?, value, default, saved }`: `value` is what
 * every app shows now (the saved row, else the default), `saved` whether a
 * row exists. The page edits `value`; Save sends only the changed keys to
 * PUT /api/admin/link-settings as `{ KEY: "value" | "" | null }`:
 *  - "" = empty: the link, address or line is hidden everywhere;
 *  - null = "Use default": the row is deleted and the default applies.
 * Old → new is listed before Save (PlanChangesBar), the same rule as Plans.
 *
 * `validateLinkValue` is the server's check (BACKEND linkSettings.mjs), so a
 * bad value is caught at the field; the server checks again and its 400
 * `INVALID_LINK_VALUE { invalid: [{ key, reason }] }` is shown per field.
 *
 * Pure (no imports) so node:test can load it.
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

export type LinkKind = "url" | "email" | "phone" | "text" | "version" | "choice" | "bool";

export interface LinkSetting {
  key: string;
  label: string;
  kind: LinkKind | string;
  options?: string[];
  value: string;
  default: string;
  saved: boolean;
}

export interface LinkSection {
  id: string;
  label: string;
  settings: LinkSetting[];
}

/** The page's own group names (DES 24-10-2026 brief). */
export const SECTION_LABELS: Record<string, string> = {
  downloads: "Downloads",
  sites: "Sites",
  contact: "Contact",
  company: "Company",
  social: "Social",
  app: "App versions",
  features: "Shown to users",
};

/** One line under each group. */
export const SECTION_HINTS: Record<string, string> = {
  downloads: "Store and download links. Empty = the badge or line is hidden in every app, email and the website.",
  sites: "Pages the apps and emails link to.",
  contact: "Shown on the website, in emails and in the apps.",
  company: "The legal line in emails and invoices shows once the registered address is saved.",
  social: "Empty = hidden. Each network shows as its icon in the apps and on the website.",
  app: "Oldest version that may still sign in. Empty = no forced update. Never above the version live in the store.",
  features: "What users see in the apps.",
};

export const CHOICE_WORDS: Record<string, string> = {
  live: "Live",
  coming_soon: "Coming soon",
  hidden: "Hidden",
};

/** Form value per key. null = "Use default" (delete the row). */
export type LinkFormValues = Record<string, string | null>;

export function linkSettingsToForm(sections: LinkSection[]): LinkFormValues {
  const out: LinkFormValues = {};
  for (const s of sections) for (const x of s.settings) out[x.key] = x.value ?? "";
  return out;
}

const EMAIL_RE = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;

/** Same check as the server (BACKEND linkSettings.mjs validateLinkValue). null = fine. */
export function validateLinkValue(s: Pick<LinkSetting, "kind" | "options">, raw: string): string | null {
  const value = raw.trim();
  if (value === "") {
    if (s.kind === "bool") return "Must be on or off";
    if (s.kind === "choice") return "Pick one";
    return null;
  }
  switch (s.kind) {
    case "url": {
      if (value.length > 500 || /\s/.test(value)) return "Must be one https link";
      try {
        const u = new URL(value);
        if (u.protocol !== "https:" || !u.hostname.includes(".")) return "Must start with https://";
      } catch {
        return "Must be a full https link";
      }
      return null;
    }
    case "email":
      return value.length <= 200 && EMAIL_RE.test(value) ? null : "Must be an email address";
    case "phone":
      return /^\+?[0-9][0-9 ()-]{5,23}$/.test(value) ? null : "Digits, spaces, + ( ) - only";
    case "version":
      return /^\d{1,4}(\.\d{1,4}){0,2}$/.test(value) ? null : "A version like 1.4 or 1.4.2";
    case "choice":
      return s.options?.includes(value) ? null : "Pick one";
    case "bool":
      return value === "true" || value === "false" ? null : "Must be on or off";
    default:
      // eslint-disable-next-line no-control-regex -- the server refuses control characters too
      return value.length <= 300 && !/[<>\u0000-\u001f]/.test(value) ? null : "Plain text, 300 characters at most";
  }
}

/** What the review list shows for a value. */
export function readableLink(s: Pick<LinkSetting, "kind" | "default">, v: string | null): string {
  if (v === null) return `Default (${readableLink(s, s.default)})`;
  const t = v.trim();
  if (s.kind === "bool") return t === "true" ? "On" : "Off";
  if (s.kind === "choice") return CHOICE_WORDS[t] ?? t;
  if (t === "") return s.kind === "version" ? "Empty (no forced update)" : "Empty (hidden)";
  return t;
}

export interface LinkChange {
  key: string;
  label: string;
  from: string;
  to: string;
}

export interface ChangedLinkSettings {
  /** The PUT body's `settings`: only changed keys. */
  settings: Record<string, string | null>;
  invalid: string[];
  lines: LinkChange[];
}

/**
 * Only keys whose value changes are sent. "Use default" on a saved row is a
 * change even when the default equals the value (the row goes); on a row
 * that isn't saved it is nothing.
 */
export function changedLinkSettings(
  sections: LinkSection[],
  current: LinkFormValues,
): ChangedLinkSettings {
  const settings: Record<string, string | null> = {};
  const invalid: string[] = [];
  const lines: LinkChange[] = [];
  for (const sec of sections) {
    for (const s of sec.settings) {
      if (!(s.key in current)) continue;
      const before = (s.value ?? "").trim();
      const after = current[s.key];
      const group = SECTION_LABELS[sec.id] ?? sec.label;
      const label = `${group}: ${s.label}`;
      if (after === null) {
        if (!s.saved) continue;
        settings[s.key] = null;
        lines.push({ key: s.key, label, from: readableLink(s, before), to: readableLink(s, null) });
        continue;
      }
      const next = after.trim();
      if (next === before) continue;
      if (validateLinkValue(s, next)) {
        invalid.push(s.key);
        continue;
      }
      // The server lower-cases emails; compare the same way.
      if (s.kind === "email" && next.toLowerCase() === before.toLowerCase()) continue;
      const stored = s.kind === "email" ? next.toLowerCase() : next;
      settings[s.key] = stored;
      lines.push({ key: s.key, label, from: readableLink(s, before), to: readableLink(s, stored) });
    }
  }
  return { settings, invalid, lines };
}

/** Tab order (W20); a group the server adds later goes last. */
export const TAB_ORDER = ["downloads", "sites", "contact", "company", "social", "app", "features"];

export function sortTabSections<T extends { id: string }>(sections: T[]): T[] {
  const rank = (id: string) => {
    const i = TAB_ORDER.indexOf(id);
    return i === -1 ? TAB_ORDER.length : i;
  };
  return [...sections].sort((a, b) => rank(a.id) - rank(b.id));
}

/** Social networks in the order every client shows them (B24 contract line, hard-coded, not JSON key order). */
export const SOCIAL_ORDER = [
  "LINKEDIN_URL",
  "INSTAGRAM_URL",
  "FACEBOOK_URL",
  "TIKTOK_URL",
  "YOUTUBE_URL",
  "X_URL",
  "THREADS_URL",
  "WHATSAPP_URL",
];

/** Settings of the Social group in SOCIAL_ORDER; an unknown key goes last. A backend before B24 just lists fewer. */
export function sortSocialSettings<T extends { key: string }>(settings: T[]): T[] {
  const rank = (k: string) => {
    const i = SOCIAL_ORDER.indexOf(k);
    return i === -1 ? SOCIAL_ORDER.length : i;
  };
  return [...settings].sort((a, b) => rank(a.key) - rank(b.key));
}

/** The open tab from a URL hash; unknown or empty = the first tab. */
export function resolveLinkTab(hash: string, ids: string[]): string {
  let id = hash.replace(/^#/, "");
  try {
    id = decodeURIComponent(id);
  } catch {
    /* keep the raw hash */
  }
  return ids.includes(id) ? id : (ids[0] ?? "");
}

/**
 * Unsaved changes per tab: the review list's keys plus typed-but-invalid and
 * server-refused keys, so the count and the one review bar read the same draft.
 */
export function unsavedByTab(
  sections: LinkSection[],
  changes: Pick<ChangedLinkSettings, "lines" | "invalid">,
  serverInvalidKeys: string[] = [],
): Record<string, number> {
  const sectionOf = new Map(sections.flatMap((sec) => sec.settings.map((x) => [x.key, sec.id] as const)));
  const keys = new Set([...changes.lines.map((l) => l.key), ...changes.invalid, ...serverInvalidKeys]);
  const out: Record<string, number> = {};
  for (const k of keys) {
    const id = sectionOf.get(k);
    if (id) out[id] = (out[id] ?? 0) + 1;
  }
  return out;
}

/** 400 INVALID_LINK_VALUE → { KEY: reason }. */
export function invalidFromServer(raw: unknown): Record<string, string> {
  const list = (raw as { invalid?: unknown } | null)?.invalid;
  if (!Array.isArray(list)) return {};
  const out: Record<string, string> = {};
  for (const x of list) {
    if (x && typeof x === "object" && typeof (x as { key?: unknown }).key === "string") {
      const r = (x as { reason?: unknown }).reason;
      out[(x as { key: string }).key] = typeof r === "string" ? r : "Not accepted";
    }
  }
  return out;
}

/** Activity log line for `setting.link_changed` { key, from, to }. */
export function describeLinkChange(d: Record<string, unknown>): string {
  const key = typeof d.key === "string" ? d.key : "a link";
  const show = (v: unknown) => (v == null ? "default" : String(v).trim() === "" ? "empty" : String(v));
  return `Changed ${key} (Links & company info): ${show(d.from)} → ${show(d.to)}`;
}
